(() => {
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  class EditorialPanel extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-no-image') || this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.classList.add('has-js');
      this.querySelectorAll('.nc-editorial-toggle').forEach(button => {
        button.hidden = false;
        button.addEventListener('click', () => this.select(this.active === button.dataset.panelId ? null : button.dataset.panelId), options);
      });
      this.addEventListener('keydown', event => { if (event.key === 'Escape') this.select(null); }, options);
    }
    select(id) {
      this.active = id;
      this.classList.toggle('has-open', !!id);
      this.querySelectorAll('.nc-editorial-toggle').forEach(button => {
        const active = button.dataset.panelId === id;
        button.setAttribute('aria-expanded', String(active));
        button.querySelector('span').textContent = active ? '−' : '+';
      });
      this.querySelectorAll('.nc-editorial-panel').forEach(panel => {
        const active = panel.dataset.panel === id;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
        panel.inert = !active;
      });
    }
    disconnectedCallback() { this.abort?.abort(); this.ready = false; }
  }
  class SeenHere extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.track = this.querySelector('.nc-video-track');
      this.videos = [...this.querySelectorAll('video')];
      this.videos.forEach(video => {
        video.muted = true;
        const tile = video.closest('.nc-video-tile');
        const button = tile.querySelector('.nc-video-toggle');
        const sync = () => {
          button.setAttribute('aria-label', `${video.paused ? 'Play' : 'Pause'} ${button.dataset.videoLabel}`);
          button.querySelector('[data-video-play]').toggleAttribute('hidden', !video.paused);
          button.querySelector('[data-video-pause]').toggleAttribute('hidden', video.paused);
        };
        button.addEventListener('click', async () => {
          if (!video.paused) { video.pause(); return; }
          document.querySelectorAll('nc-seen-here video').forEach(other => { if (other !== video) other.pause(); });
          if (!video.dataset.loaded) {
            video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
            video.load(); video.dataset.loaded = 'true';
          }
          video.muted = true;
          const error = tile.querySelector('.nc-video-error');
          error.hidden = true;
          try { await video.play(); } catch (failure) {
            if (failure.name !== 'AbortError') { error.textContent = 'Video unavailable. Tap to retry.'; error.hidden = false; }
          }
          sync();
        }, options);
        video.addEventListener('play', sync, options);
        video.addEventListener('pause', sync, options);
        video.addEventListener('volumechange', () => { if (!video.muted) video.muted = true; }, options);
      });
      this.observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.intersectionRatio < .35) entry.target.pause();
      }), {threshold: [.0, .35]});
      this.videos.forEach(video => this.observer.observe(video));
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.videos.forEach(video => video.pause()); }, options);
      const update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth;
        this.querySelector('[data-video-arrows]').hidden = max <= 2;
        this.querySelector('[data-video-prev]').disabled = this.track.scrollLeft < 2;
        this.querySelector('[data-video-next]').disabled = this.track.scrollLeft >= max - 2;
      };
      ['prev', 'next'].forEach(direction => this.querySelector(`[data-video-${direction}]`).addEventListener('click', () => {
        this.track.scrollBy({left: (direction === 'prev' ? -1 : 1) * this.track.clientWidth * .8, behavior: reduced() ? 'instant' : 'smooth'});
      }, options));
      this.track.addEventListener('scroll', update, options);
      this.resize = new ResizeObserver(update); this.resize.observe(this.track); update();
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.resize?.disconnect(); this.videos?.forEach(v => v.pause()); this.ready = false; }
  }
  class Reviews extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true; this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.track = this.querySelector('.nc-review-track');
      this.manualPause = false; this.hover = false; this.focused = false; this.visible = false;
      const controls = this.querySelector('.nc-review-controls');
      const update = () => { controls.hidden = this.track.scrollWidth <= this.track.clientWidth + 2; };
      this.resize = new ResizeObserver(update); this.resize.observe(this.track); update();
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; }, {threshold: 0}); this.observer.observe(this);
      this.addEventListener('mouseenter', () => { this.hover = true; }, options);
      this.addEventListener('mouseleave', () => { this.hover = false; }, options);
      this.addEventListener('focusin', () => { this.focused = true; }, options);
      this.addEventListener('focusout', event => { this.focused = this.contains(event.relatedTarget); }, options);
      const pause = this.querySelector('[data-review-pause]');
      pause?.addEventListener('click', () => {
        this.manualPause = !this.manualPause;
        pause.textContent = this.manualPause ? 'Play' : 'Pause';
        pause.setAttribute('aria-label', `${this.manualPause ? 'Resume' : 'Pause'} review movement`);
      }, options);
      ['prev', 'next'].forEach(direction => this.querySelector(`[data-review-${direction}]`).addEventListener('click', () => {
        this.track.scrollBy({left: (direction === 'prev' ? -1 : 1) * 312, behavior: reduced() ? 'instant' : 'smooth'});
      }, options));
      let previous = 0, accumulated = 0;
      const tick = time => {
        const delta = Math.min(time - previous, 60); previous = time;
        if (this.dataset.marquee === 'true' && window.innerWidth >= 750 && !reduced() && !document.hidden && this.visible && !this.manualPause && !this.hover && !this.focused) {
          accumulated += delta * .018;
          if (accumulated >= 1) {
            this.track.scrollLeft += Math.floor(accumulated); accumulated %= 1;
            if (this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 1) this.track.scrollLeft = 0;
          }
        }
        this.frame = requestAnimationFrame(tick);
      };
      if (this.dataset.marquee === 'true') this.frame = requestAnimationFrame(tick);
    }
    disconnectedCallback() { this.abort?.abort(); this.resize?.disconnect(); this.observer?.disconnect(); cancelAnimationFrame(this.frame); this.ready = false; }
  }
  if (!customElements.get('nc-editorial-panel')) customElements.define('nc-editorial-panel', EditorialPanel);
  if (!customElements.get('nc-seen-here')) customElements.define('nc-seen-here', SeenHere);
  if (!customElements.get('nc-reviews')) customElements.define('nc-reviews', Reviews);
})();

// Cart sections reconnect this element after every quantity or discount update.
if (!customElements.get('nc-cart-estimate')) {
  customElements.define('nc-cart-estimate', class extends HTMLElement {
    connectedCallback() {
      const currency = this.dataset.currency;
      if (currency === 'AUD' || this.dataset.country === 'US' || this.dataset.supported !== 'true') return;
      const rate = Number(window.Shopify?.currency?.rate);
      if (window.Shopify?.currency?.active !== currency || !Number.isFinite(rate) || rate <= 0) return;
      const total = Number(this.dataset.total);
      const threshold = Math.ceil(Number(this.dataset.threshold) * rate);
      if (total > 0 && total >= threshold) {
        const shipping = this.querySelector('[data-nc-shipping-estimate]');
        if (shipping) shipping.textContent = 'Free';
      }
    }
  });
}
